import { inject, Injectable } from '@angular/core';
import { ProjectDto } from '@pf/platform-wms/domain';
import { PackingTask } from '../models/packing-task.model';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { ProjectPackingSummaryFactory } from '../factories/project-packing-summary.factory';
import { ProjectPackingSummary } from '../models/project-packing-summary.model';
import { groupBy, uniqBy } from 'lodash-es';
import { EquipmentStatus, SlotType } from '@control-tower/platform-wms';

@Injectable()
export class PackingTaskOrchestrator {
    private readonly taskStateService = inject(CpwTaskStateService);
    private readonly summaryFactory = inject(ProjectPackingSummaryFactory);

    orchestrate(
        pickedProjects: ProjectDto[],
        activeProjects: ProjectDto[] | null,
        showLtlProjects: boolean,
        currentProjectId?: string
    ) {
        const sortedProjects = pickedProjects
            .map(project => this.summaryFactory.create(project))
            .sort((a, b) => {
                const aEndDate = new Date(a.dueDate);
                const bEndDate = new Date(b.dueDate);
                if (aEndDate < bEndDate) return -1;
                if (aEndDate > bEndDate) return 1;
                return new Date(a.pickedAt) < new Date(b.pickedAt) ? -1 : 1;
            })
            .filter(project => showLtlProjects || !project.isLtl)
            .filter(project => this.isFullyStaged(project))
            .filter(project => !project.isPacked);

        const activeProjectIds = new Set((activeProjects ?? []).map(p => p.id));

        // Remove active projects from pending tasks
        const pendingTasks = this.getTasks(
            sortedProjects.filter(p => !activeProjectIds.has(p.id))
        );

        if (!activeProjects?.length) {
            this.taskStateService.setTasks(pendingTasks);
            return;
        }

        // Build a single active task from all claimed projects
        const activeSummaries = activeProjects.map(p =>
            this.summaryFactory.create(p)
        );
        const activeTask =
            activeSummaries.length === 1
                ? this.mapProjectToTask(
                      'active',
                      activeSummaries[0].isPriority,
                      activeSummaries[0]
                  )
                : this.mapProjectsToGroupTask(activeSummaries);

        activeTask.id = `active-${activeTask.id}`;
        activeTask.section = 'active';
        activeTask.claimedAt = activeProjects[0].modifiedDate;

        // Set summary to the project currently being worked on
        if (currentProjectId) {
            const currentSummary = activeSummaries.find(
                s => s.id === currentProjectId
            );
            if (currentSummary) {
                (activeTask as PackingTask).summary = currentSummary;
            }
        }

        pendingTasks.forEach(task => {
            task.readonly = true;
        });

        this.taskStateService.setTasks([activeTask, ...pendingTasks]);
    }

    private getTasks(projects: ProjectPackingSummary[]) {
        const priorityOrLtl = projects.filter(
            project => project.isPriority || project.isLtl
        );
        const groupable = projects.filter(
            project => !project.isPriority && !project.isLtl
        );

        const priorityTasks = priorityOrLtl.map(project =>
            this.mapProjectToTask(
                project.isPriority ? 'priority' : 'ready',
                project.isPriority,
                project
            )
        );

        const standaloneProjects: ProjectPackingSummary[] = [];
        const equipmentGroups = new Map<string, ProjectPackingSummary[]>();

        for (const project of groupable) {
            const uniqueEquipmentIds = [
                ...new Set(
                    project.lines
                        .flatMap(line => line.picks)
                        .map(
                            pick =>
                                pick.bundle.container?.equipment?.id as
                                    | string
                                    | undefined
                        )
                        .filter((id): id is string => !!id)
                ),
            ];

            if (uniqueEquipmentIds.length !== 1) {
                standaloneProjects.push(project);
                continue;
            }

            const equipmentId = uniqueEquipmentIds[0];
            if (!equipmentGroups.has(equipmentId)) {
                equipmentGroups.set(equipmentId, []);
            }
            equipmentGroups.get(equipmentId)!.push(project);
        }

        const standaloneTasks = standaloneProjects.map(project =>
            this.mapProjectToTask('ready', false, project)
        );

        const groupedTasks = Array.from(equipmentGroups.values()).map(
            projects => this.mapProjectsToGroupTask(projects)
        );

        return [...priorityTasks, ...standaloneTasks, ...groupedTasks];
    }

    private isFullyStaged(summary: ProjectPackingSummary): boolean {
        const picks = summary.lines.flatMap(line => line.picks);
        if (picks.length === 0) {
            return false;
        }

        return picks.every(pick => {
            const container = pick.bundle.container;
            if (!container) {
                return false;
            }

            // LTL - Container is assigned to a staging slot
            if (
                summary.isLtl &&
                container.slot?.slotType === SlotType.Staging
            ) {
                return true;
            }

            // Parts - Container is on equipment that is assigned to a zone
            return (
                !summary.isLtl &&
                container.equipment?.status === EquipmentStatus.Staged
            );
        });
    }

    private static getDistinctContainers(projects: ProjectPackingSummary[]) {
        return uniqBy(
            projects.flatMap(p => p.lines.flatMap(line => line.picks)),
            pick => pick.bundle.container?.id
        );
    }

    private mapProjectToTask(
        section: PackingTask['section'],
        priority: boolean,
        summary: ProjectPackingSummary
    ): PackingTask {
        const distinctContainers =
            PackingTaskOrchestrator.getDistinctContainers([summary]);
        const countsByContainerType = groupBy(
            distinctContainers,
            pick =>
                pick.bundle.container?.containerType?.name
                    ?.replace(/\d+$/, '')
                    ?.trim()
                    ?.toLowerCase() || 'bin'
        );
        const containerDetails = Object.entries(countsByContainerType)
            .map(
                ([type, bundles]) =>
                    `${bundles.length} ${type}${bundles.length > 1 ? 's' : ''}`
            )
            .join(', ');

        const isPacked = summary.isPacked;

        return {
            id: summary.id,
            warehouseId: summary.warehouseId,
            section,
            title: `${summary.orderNumber} | ${summary.projectType}`,
            details: `${summary.lines.length} items - ${containerDetails}`,
            time: summary.pickedAt,
            priority,
            taskIconType: 'packing',
            group: summary.isLtl ? 'LTL' : 'Parts',
            summary,
            readonly: false,
            isLtl: summary.isLtl,
            context: {
                projectId: summary.id,
                containersVerified: isPacked,
                itemsVerified: isPacked,
                itemsPacked: isPacked,
            },
        } as PackingTask;
    }

    private mapProjectsToGroupTask(
        projects: ProjectPackingSummary[]
    ): PackingTask {
        const firstProject = projects[0];

        const firstEquipment = firstProject.lines
            .flatMap(line => line.picks)
            .map(pick => pick.bundle.container?.equipment)
            .find(eq => !!eq);

        const equipmentBarcode = firstEquipment?.barcode ?? '';
        const zoneBarcode = firstEquipment?.zone?.barcode ?? null;

        const allDistinctContainers =
            PackingTaskOrchestrator.getDistinctContainers(projects);

        const binCount = allDistinctContainers.filter(pick => {
            const name =
                pick.bundle.container?.containerType?.name
                    ?.toLowerCase()
                    .trim() ?? '';
            return !name || name.includes('bin') || name === 'cart';
        }).length;

        const projectCount = projects.length;

        const details = `${projectCount} project${projectCount > 1 ? 's' : ''}, ${binCount} bin${binCount > 1 ? 's' : ''}`;

        const summary = firstProject;

        return {
            id: `equipment-${firstEquipment?.id ?? 'unknown'}`,
            warehouseId: summary.warehouseId,
            section: 'ready',
            title: `${equipmentBarcode}${zoneBarcode ? ' | ' + zoneBarcode : ''}`,
            details,
            time: summary.pickedAt,
            priority: false,
            taskIconType: 'packing',
            group: 'Parts',
            summary,
            readonly: false,
            isLtl: false,
            isMultiProject: true,
            projects,
            equipmentBarcode,
            zoneBarcode,
            context: {
                projectId: summary.id,
                containersVerified: false,
                itemsVerified: false,
                itemsPacked: false,
                assignedHandlingUnitIds: [],
                latestHandlingUnitId: '',
            },
        } as PackingTask;
    }
}
